// Formik
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

// Material UI
import { Button, Stack } from "@mui/material";

// Validation
import { mbsNewPasswordValidationSchema } from "Validation/UserValidation";

// Common
import { FormikTextField } from "Common/Components";

// Auth
import { useMBSAuth } from "Auth/MBS/MBSAuthContext";

// Components
import MBSAuthFormHeader from "./MBSAuthFormHeader";
import MBSPasswordFields from "./MBSPasswordFields";

const MBSNewPasswordForm = ({
  onSubmit,
  onBackToSignIn,
  temporaryPassword = "",
}) => {
  const { t } = useTranslation();
  const { requiredAttributes } = useMBSAuth();
  const requiresNameFields =
    requiredAttributes.includes("given_name") ||
    requiredAttributes.includes("family_name");
  const formik = useFormik({
    initialValues: {
      userFirstName: "",
      userLastName: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: mbsNewPasswordValidationSchema(t, {
      requiresNameFields,
      temporaryPassword,
    }),
    onSubmit: async (values) => {
      const userAttributes = requiresNameFields
        ? {
            given_name: values.userFirstName.trim(),
            family_name: values.userLastName.trim(),
          }
        : {};

      await onSubmit(values.newPassword, userAttributes);
    },
  });
  const newPasswordHelperError =
    formik.touched.newPassword &&
    formik.errors.newPassword ===
      t("mbsAuth.errors.newPasswordMustDifferFromTemporary")
      ? formik.errors.newPassword
      : undefined;

  return (
    <Stack
      component="form"
      noValidate
      onSubmit={formik.handleSubmit}
      spacing={3}
    >
      <MBSAuthFormHeader title={t("mbsAuth.newPasswordRequiredMessage")} />
      {requiresNameFields && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 2, sm: 1 }}
        >
          <FormikTextField
            formik={formik}
            name="userFirstName"
            field={{
              label: t("mbsAuth.firstName"),
            }}
          />
          <FormikTextField
            formik={formik}
            name="userLastName"
            field={{
              label: t("mbsAuth.lastName"),
            }}
          />
        </Stack>
      )}
      <MBSPasswordFields
        formik={formik}
        newPasswordHelperError={newPasswordHelperError}
      />
      <Stack spacing={2} alignItems="center">
        <Button
          type="submit"
          variant="contained"
          disabled={formik.isSubmitting}
          fullWidth
        >
          {formik.isSubmitting
            ? t("mbsAuth.settingPassword")
            : t("mbsAuth.setNewPassword")}
        </Button>
        <Button
          type="button"
          variant="text"
          onClick={() => {
            formik.resetForm();
            onBackToSignIn();
          }}
          disabled={formik.isSubmitting}
        >
          {t("mbsAuth.backToSignIn")}
        </Button>
      </Stack>
    </Stack>
  );
};

export default MBSNewPasswordForm;
